// resources/js/hooks/useSipDevice.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import * as JsSIP from 'jssip';
import { router } from '@inertiajs/react';
import * as voipApi from '@/lib/voip/api';
import type { SipDeviceState, SipCallState, SoftphoneTokenResponse } from '@/types/voip';

// Type augmentation for JsSIP Session
interface JsSIPSession extends JsSIP.Session {
  _remote_identity?: { uri: { user: string; host: string } };
}

// Global SIP device instance to persist across component remounts
let globalUa: JsSIP.UA | null = null;
let globalDeviceState: SipDeviceState = 'offline';
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let currentNonce: string | null = null;

export interface UseSipDeviceReturn {
  deviceState: SipDeviceState;
  error: string | null;
  currentSession: JsSIP.Session | null;
  currentCallState: SipCallState;
  makeCall: (phoneNumber: string, callId?: number) => Promise<JsSIP.Session | null>;
  hangup: () => void;
  acceptCall: () => void;
  rejectCall: () => void;
  sendDTMF: (digit: string) => void;
  toggleMute: () => void;
  reinitialize: () => Promise<void>;
}

// Helper to map JsSIP session state to our internal state
function mapSessionState(state: string | undefined): SipCallState {
  switch (state) {
    case undefined:
    case 'null':
      return 'idle';
    case 'connecting':
      return 'ringing';
    case 'progress':
      return 'ringing';
    case 'early':
      return 'ringing';
    case 'confirmed':
      return 'answered';
    case 'ended':
      return 'hangup';
    default:
      return 'idle';
  }
}

export function useSipDevice(
  arg: number | null | { sipAccountId: number | null; createCallRow?: (phoneNumber: string) => Promise<number> }
): UseSipDeviceReturn {
  // Accept both signatures:
  //   useSipDevice(9)
  //   useSipDevice({ sipAccountId: 9, createCallRow: fn })
  const sipAccountId: number | null =
    typeof arg === 'object' && arg !== null
      ? arg.sipAccountId
      : (arg as number | null);

  // createCallRow lets the caller (e.g. VoipProvider) decide how the
  // backend Call row is created before the SIP INVITE. Without this,
  // POST /call-sessions fails authorization (call_id is required).
  const createCallRow: ((phoneNumber: string) => Promise<number>) | undefined =
    typeof arg === 'object' && arg !== null
      ? arg.createCallRow
      : undefined;

  const createCallRowRef = useRef(createCallRow);
  useEffect(() => {
    createCallRowRef.current = createCallRow;
  }, [createCallRow]);

  const [deviceState, setDeviceState] = useState<SipDeviceState>(globalDeviceState);
  const [error, setError] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<JsSIP.Session | null>(null);
  const [currentCallState, setCurrentCallState] = useState<SipCallState>('idle');
  const [callStartedAt, setCallStartedAt] = useState<number>(0);
  const [callRemote, setCallRemote] = useState<string>('');
  const [callDirection, setCallDirection] = useState<'inbound' | 'outbound'>('outbound');
  
  const uaRef = useRef<JsSIP.UA | null>(globalUa);
  const currentSessionRef = useRef<JsSIP.Session | null>(null);
  const isMutedRef = useRef(false);
  const isInitializing = useRef(false);
  const sipAccountIdRef = useRef(sipAccountId);

  // Lazily create a single <audio> element for remote stream playback.
  // WebRTC media won't be heard unless its MediaStream is attached to an
  // HTMLAudioElement and that element is playing.
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  if (typeof document !== 'undefined' && !remoteAudioRef.current) {
    let el = document.getElementById('teleman-sip-remote-audio') as HTMLAudioElement | null;
    if (!el) {
      el = document.createElement('audio');
      el.id = 'teleman-sip-remote-audio';
      el.autoplay = true;
      (el as any).playsInline = true;
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    remoteAudioRef.current = el;
  }

  // Update global state when local state changes
  useEffect(() => {
    globalUa = uaRef.current;
    globalDeviceState = deviceState;
  }, [deviceState]);

  // Refresh token periodically (every 4 minutes, token TTL is 5 min)
  const scheduleTokenRefresh = useCallback((nonce: string, accountId: number) => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const refreshed = await voipApi.refreshSoftphoneToken(accountId, nonce);
        if (uaRef.current && refreshed.config) {
          currentNonce = refreshed.nonce;
          // NOTE: JsSIP 3 removed setConfiguration/getConfiguration. To rotate
          // credentials we would need to destroy and re-create the UA, which
          // briefly disconnects the device. For now we just log the refresh
          // and rely on the next initializeDevice() (e.g. after reconnect)
          // to pick up new credentials.
          console.log('SIP softphone token refreshed (UA will pick up new credentials on next re-init).');
          scheduleTokenRefresh(refreshed.nonce, accountId);
        }
      } catch (err) {
        console.error('Failed to refresh SIP token:', err);
        // If refresh fails, schedule retry sooner
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => {
          if (sipAccountIdRef.current) {
            scheduleTokenRefresh(nonce, sipAccountIdRef.current);
          }
        }, 60000); // Retry after 1 minute
      }
    }, 4 * 60 * 1000); // Refresh every 4 minutes
  }, []);

  // Initialize SIP device
  const initializeDevice = useCallback(async () => {
    if (!sipAccountIdRef.current) {
      console.log('No SIP account ID provided, skipping initialization');
      setDeviceState('offline');
      globalDeviceState = 'offline';
      return;
    }

    // If already initialized and registered, reuse
    if (uaRef.current && deviceState === 'registered') {
      console.log('Reusing existing SIP UA');
      return;
    }

    if (isInitializing.current) {
      console.log('SIP initialization already in progress');
      return;
    }

    // Anti-loop guard: if we already initialized this account in this session
    // and it failed, do NOT auto-retry. The user must press a refresh button.
    // This prevents accidental request-floods that trigger 429 from rate limiter.
    const lastAttemptKey = `__sip_last_attempt_${sipAccountIdRef.current}`;
    const lastAttempt = (window as any)[lastAttemptKey];
    const now = Date.now();
    if (lastAttempt && (now - lastAttempt) < 5000) {
      console.warn(`Refusing to re-initialize SIP account ${sipAccountIdRef.current} — last attempt was ${now - lastAttempt}ms ago. Wait 5s.`);
      return;
    }
    (window as any)[lastAttemptKey] = now;

    isInitializing.current = true;
    setDeviceState('registering');
    setError(null);

    try {
      // Get softphone token from backend.
      // Backend wraps payload in { success, data: {...}, message }, so we
      // accept either the raw payload (legacy) or the unwrapped data field.
      const tokenResponse: any = await voipApi.getSoftphoneToken(sipAccountIdRef.current);
      const tokenData = tokenResponse?.data ?? tokenResponse;

      if (!tokenData || !tokenData.config) {
        throw new Error(
          'Invalid softphone token response from backend. Expected { config, nonce } payload. Got: '
          + JSON.stringify(tokenResponse).substring(0, 200)
        );
      }

      const { config, nonce } = tokenData;
      currentNonce = nonce;

      // The backend (SipCredentials::toJsSipConfig) returns a config that
      // is already in JsSIP format. We just have to wrap WebSocket URIs
      // into WebSocketInterface objects.
      //
      // Expected shape:
      //   {
      //     uri: 'sip:user@host',
      //     password: '...',
      //     display_name: '...',
      //     sockets: ['wss://...'],            <-- array of strings
      //     realm: '...',
      //     authorization_user: '...',
      //     register: true,
      //     session_timers: false,
      //     pcConfig: { iceServers: [...] }
      //   }
      const wsUris: string[] = Array.isArray(config.sockets) ? config.sockets : [];
      if (wsUris.length === 0) {
        throw new Error(
          'SIP softphone config has no WebSocket sockets. The SIP account needs a ws_uri (e.g. wss://pbx.example.com:8089/ws).'
        );
      }

      const socketObjects = wsUris.map((u: string) => new JsSIP.WebSocketInterface(u));

      const uaConfig: JsSIP.Configuration = {
        sockets: socketObjects,
        uri: config.uri,
        password: config.password,
        display_name: config.display_name,
        authorization_user: config.authorization_user,
        realm: config.realm || undefined,
        register: config.register ?? true,
        register_expires: 600,
        session_timers: config.session_timers ?? false,
        connection_recovery_min_interval: 2,
        connection_recovery_max_interval: 30,
        use_preloaded_route: false,
        no_answer_timeout: 60,
      };

      // Pass ICE/STUN/TURN configuration if provided
      if (config.pcConfig) {
        (uaConfig as any).pcConfig = config.pcConfig;
      }

      // Add STUN/TURN if provided
      if (config.stun_server) {
        // JsSIP uses RTCConfiguration format for ICE servers
      }

      const ua = new JsSIP.UA(uaConfig);

      // Set up event listeners
      ua.on('connected', () => {
        console.log('SIP UA connected');
        setDeviceState('registered');
        globalDeviceState = 'registered';
        setError(null);
        
        // Report successful registration to backend
        voipApi.reportRegistrationEvent(sipAccountIdRef.current!, {
          check_type: 'register',
          status: 'success',
          sip_code: 200,
        }).catch(err => console.error('Failed to report registration:', err));
      });

      ua.on('disconnected', (e) => {
        console.log('SIP UA disconnected', e);
        setDeviceState('offline');
        globalDeviceState = 'offline';
        
        voipApi.reportRegistrationEvent(sipAccountIdRef.current!, {
          check_type: 'register',
          status: 'failed',
          error: 'Disconnected',
        }).catch(() => {});
      });

      ua.on('failed', (e) => {
        console.error('SIP UA failed', e);
        setDeviceState('error');
        globalDeviceState = 'error';
        setError('Connection failed. Please check your network.');
        
        voipApi.reportRegistrationEvent(sipAccountIdRef.current!, {
          check_type: 'register',
          status: 'failed',
          error: typeof e === 'string' ? e : 'Connection failed',
        }).catch(() => {});
      });

      ua.on('newRTCSession', (data: { session: JsSIPSession }) => {
        const session = data.session;
        const direction = session.direction;

        // Centralized function to play remote audio. Idempotent — safe to
        // call multiple times. Tries to find the RTCPeerConnection from
        // wherever JsSIP put it.
        const attachRemoteAudio = (label: string) => {
          const pc: RTCPeerConnection | null =
            (session as any).connection ||
            (session as any)._connection ||
            null;

          if (!pc) {
            console.warn(`SIP[${label}]: no RTCPeerConnection found on session yet`);
            return;
          }

          // 1) Listen for future 'track' events
          if (!(pc as any).__telemanTrackAttached) {
            (pc as any).__telemanTrackAttached = true;
            pc.addEventListener('track', (e: RTCTrackEvent) => {
              console.log(`SIP[${label}]: remote track event fired (${e.track.kind})`);
              if (e.track.kind !== 'audio') return;
              if (!remoteAudioRef.current) return;
              const stream = e.streams?.[0] || new MediaStream([e.track]);
              remoteAudioRef.current.srcObject = stream;
              remoteAudioRef.current.play()
                .then(() => console.log(`SIP[${label}]: remote audio playing`))
                .catch((err) => console.warn(`SIP[${label}]: autoplay blocked:`, err));
            });
          }

          // 2) Also handle the case where tracks already arrived
          //    (peer connection had time to receive them before listener attached).
          try {
            const receivers = pc.getReceivers();
            const audioReceiver = receivers.find(r => r.track?.kind === 'audio');
            if (audioReceiver?.track) {
              console.log(`SIP[${label}]: found existing audio receiver, attaching now`);
              if (remoteAudioRef.current) {
                const stream = new MediaStream([audioReceiver.track]);
                remoteAudioRef.current.srcObject = stream;
                remoteAudioRef.current.play()
                  .then(() => console.log(`SIP[${label}]: remote audio playing (existing track)`))
                  .catch((err) => console.warn(`SIP[${label}]: autoplay blocked (existing):`, err));
              }
            }
          } catch (err) {
            console.warn(`SIP[${label}]: getReceivers failed:`, err);
          }
        };

        // Common terminal-state cleanup
        const handleEnd = (label: string) => {
          console.log(`SIP session ${label}`);
          setCurrentCallState(label === 'failed' ? 'failed' : 'hangup');
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = null;
          }
          setTimeout(() => {
            setCurrentSession(null);
            currentSessionRef.current = null;
            setCurrentCallState('idle');
            setCallStartedAt(0);
            setCallRemote('');
          }, 1000);
        };

        if (direction === 'incoming') {
          const remoteUser = session._remote_identity?.uri?.user ?? 'unknown';
          console.log('Incoming call from:', remoteUser);
          setCurrentSession(session);
          currentSessionRef.current = session;
          setCurrentCallState('incoming');
          setCallRemote(remoteUser);
          setCallDirection('inbound');
        } else {
          // Outgoing
          const remoteUser =
            session._remote_identity?.uri?.user
            || (session as any)?._request?.ruri?.user
            || 'unknown';
          setCurrentSession(session);
          currentSessionRef.current = session;
          setCurrentCallState('ringing');
          setCallRemote(remoteUser);
          setCallDirection('outbound');
        }

        // Peer connection is created as soon as we have an SDP exchange.
        session.on('peerconnection', (e: any) => {
          console.log('SIP: peerconnection event fired');
          // If the event payload has the pc directly, wire it now
          if (e?.peerconnection) {
            const pc: RTCPeerConnection = e.peerconnection;
            if (!(pc as any).__telemanTrackAttached) {
              (pc as any).__telemanTrackAttached = true;
              pc.addEventListener('track', (te: RTCTrackEvent) => {
                console.log(`SIP[pc-event]: remote track (${te.track.kind})`);
                if (te.track.kind !== 'audio') return;
                if (!remoteAudioRef.current) return;
                const stream = te.streams?.[0] || new MediaStream([te.track]);
                remoteAudioRef.current.srcObject = stream;
                remoteAudioRef.current.play()
                  .then(() => console.log('SIP[pc-event]: audio playing'))
                  .catch((err) => console.warn('SIP[pc-event]: autoplay blocked:', err));
              });
            }
          }
          attachRemoteAudio('peerconnection');
        });

        // 1xx provisional responses (e.g. 180 Ringing)
        session.on('progress', () => {
          console.log('SIP: 1xx progress (ringing)');
          setCurrentCallState('ringing');
          attachRemoteAudio('progress'); // early-attach for early media
        });

        // 200 OK received (call answered) — this is where startedAt should set
        session.on('accepted', () => {
          console.log('SIP: call accepted (200 OK)');
          setCurrentCallState('answered');
          setCallStartedAt(Date.now());
          attachRemoteAudio('accepted');
        });

        // ACK exchanged (call fully established)
        session.on('confirmed', () => {
          console.log('SIP: call confirmed (ACK)');
          setCurrentCallState('answered');
          // Set startedAt if not already set (e.g. if accepted didn't fire)
          setCallStartedAt((prev) => prev || Date.now());
          attachRemoteAudio('confirmed');
        });

        session.on('failed', (e: any) => {
          console.error('Call failed:', e);
          setError(`Call failed: ${e?.cause || 'unknown'}`);
          handleEnd('failed');
        });

        session.on('ended', () => {
          console.log('SIP: call ended');
          handleEnd('ended');
        });

        // Hold/unhold events for completeness
        session.on('hold', () => console.log('SIP: call on hold'));
        session.on('unhold', () => console.log('SIP: call resumed'));
      });

      ua.on('newMessage', () => {});
      ua.on('sipEvent', () => {});

      ua.start();
      uaRef.current = ua;
      
      // Schedule token refresh
      scheduleTokenRefresh(nonce, sipAccountIdRef.current);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize SIP device';
      console.error('SIP initialization error:', err);
      setError(errorMessage);
      setDeviceState('error');
      globalDeviceState = 'error';
    } finally {
      isInitializing.current = false;
    }
  }, [deviceState, scheduleTokenRefresh]);

  // Make outbound call
  const makeCall = useCallback(async (phoneNumber: string, callId?: number): Promise<JsSIP.Session | null> => {
    if (!uaRef.current || deviceState !== 'registered') {
      setError('Device not ready. Please wait for registration.');
      return null;
    }

    if (currentSessionRef.current) {
      setError('Already in a call. Please hang up first.');
      return null;
    }

    try {
      // Tell the backend a SIP session is starting.
      // If caller supplied a callId, use it (campaign-driven flow).
      // Otherwise the backend will auto-create a Call row for this ad-hoc call.
      const sessionPayload: any = {
        sip_account_id: sipAccountIdRef.current!,
        to: phoneNumber,
        driver: 'generic_sip',
      };

      if (callId) {
        sessionPayload.call_id = callId;
      } else if (createCallRowRef.current) {
        // Optional: caller can still provide a custom factory.
        const resolvedCallId = await createCallRowRef.current(phoneNumber);
        if (resolvedCallId) {
          sessionPayload.call_id = resolvedCallId;
        }
      }

      const sessionData: any = await voipApi.startCallSession(sessionPayload);

      // Extract target URI. We need the SIP domain to build sip:NUMBER@DOMAIN
      // when the caller just typed a raw number.
      let targetUri = phoneNumber;
      if (!targetUri.includes('@')) {
        // JsSIP 3 doesn't have getConfiguration() — use the configuration
        // property directly. Fall back to parsing the registered URI.
        const ua: any = uaRef.current;
        let domain = '';
        try {
          domain =
            ua?.configuration?.uri?.host
            || ua?._configuration?.uri?.host
            || ua?.registrator?.()?.uri?.host
            || '';
        } catch { /* swallow */ }

        // Final fallback: parse from the SipCredentials URI we built earlier.
        if (!domain && ua?._configuration?.uri) {
          const uriStr = ua._configuration.uri.toString?.() ?? String(ua._configuration.uri);
          const m = uriStr.match(/@([^;>\s]+)/);
          if (m) domain = m[1];
        }

        targetUri = domain ? `sip:${phoneNumber}@${domain}` : `sip:${phoneNumber}`;
      }

      const session = uaRef.current.call(targetUri, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: {
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
            { urls: "stun:stun.cloudflare.com:3478" }
          ]
        },
      });

      // Store call session ID on the session for later use
      (session as any).callSessionId = sessionData?.data?.id ?? sessionData?.id;

      return session;
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message
        || (err instanceof Error ? err.message : 'Failed to make call');
      console.error('Make call error:', err);
      setError(errorMessage);
      return null;
    }
  }, [deviceState]);

  // Hang up current call
  const hangup = useCallback(() => {
    if (currentSessionRef.current) {
      try {
        currentSessionRef.current.terminate();
        currentSessionRef.current = null;
        setCurrentSession(null);
        setCurrentCallState('idle');
        
        // Report end to backend
        const sessionId = (currentSessionRef.current as any)?.callSessionId;
        if (sessionId) {
          voipApi.endCallSession(sessionId, { cause: 'hangup' }).catch(() => {});
        }
      } catch (err) {
        console.error('Hangup error:', err);
      }
    }
  }, []);

  // Accept incoming call
  const acceptCall = useCallback(() => {
    if (currentSessionRef.current && currentCallState === 'incoming') {
      try {
        currentSessionRef.current.answer({
          mediaConstraints: { audio: true, video: false },
        });
      } catch (err) {
        console.error('Accept call error:', err);
      }
    }
  }, [currentCallState]);

  // Reject incoming call
  const rejectCall = useCallback(() => {
    if (currentSessionRef.current && currentCallState === 'incoming') {
      try {
        currentSessionRef.current.terminate();
        currentSessionRef.current = null;
        setCurrentSession(null);
        setCurrentCallState('idle');
      } catch (err) {
        console.error('Reject call error:', err);
      }
    }
  }, [currentCallState]);

  // Send DTMF
  const sendDTMF = useCallback((digit: string) => {
    if (currentSessionRef.current && currentCallState === 'answered') {
      try {
        currentSessionRef.current.sendDTMF(digit);
      } catch (err) {
        console.error('DTMF error:', err);
      }
    }
  }, [currentCallState]);

  // Toggle mute (manipulates the outgoing audio track's enabled flag)
  const toggleMute = useCallback(() => {
    const session = currentSessionRef.current;
    if (!session) {
      console.warn('Cannot toggle mute: no active session');
      return;
    }

    const pc: RTCPeerConnection | null =
      (session as any).connection ||
      (session as any)._connection ||
      null;

    if (!pc) {
      console.warn('Cannot toggle mute: no peer connection');
      return;
    }

    isMutedRef.current = !isMutedRef.current;
    const shouldMute = isMutedRef.current;

    // Mute outgoing audio tracks
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !shouldMute;
        console.log(`SIP: outgoing audio ${shouldMute ? 'muted' : 'unmuted'}`);
      }
    });

    // Also use JsSIP's mute API if available — sends INFO to peer
    try {
      if (shouldMute) {
        (session as any).mute?.({ audio: true });
      } else {
        (session as any).unmute?.({ audio: true });
      }
    } catch (err) {
      // JsSIP mute may not be available in all versions
    }
  }, []);

  // Reinitialize device
  const reinitialize = useCallback(async () => {
    if (uaRef.current) {
      uaRef.current.stop();
      uaRef.current = null;
      globalUa = null;
    }
    if (refreshTimer) clearTimeout(refreshTimer);
    currentNonce = null;
    setDeviceState('offline');
    setError(null);
    await new Promise(resolve => setTimeout(resolve, 500));
    await initializeDevice();
  }, [initializeDevice]);

  // Keep latest initializeDevice in a ref so the mount effect doesn't depend on it
  const initializeDeviceRef = useRef(initializeDevice);
  useEffect(() => {
    initializeDeviceRef.current = initializeDevice;
  }, [initializeDevice]);

  // Initialize on mount — depend ONLY on sipAccountId so we don't loop
  useEffect(() => {
    sipAccountIdRef.current = sipAccountId;
    if (sipAccountId) {
      initializeDeviceRef.current();
    } else {
      setDeviceState('offline');
    }

    // Cleanup on unmount
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      // Don't fully destroy UA on unmount to keep it alive during navigation
      // Only disconnect on page unload
    };
  }, [sipAccountId]);

  // Handle page unload cleanup
  useEffect(() => {
    const handleUnload = () => {
      if (uaRef.current) {
        uaRef.current.stop();
      }
      if (refreshTimer) clearTimeout(refreshTimer);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Build the enriched currentSession that the VoipProvider expects:
  // { state, remote, direction, startedAt, _jsSipSession }
  // The provider's sessionToUnifiedCall() reads these fields directly.
  const enrichedCurrentSession = currentSession
    ? ({
        _jsSipSession: currentSession,
        state: currentCallState,
        remote: callRemote,
        direction: callDirection,
        startedAt: callStartedAt,
      } as any)
    : null;

  return {
    deviceState,
    error,
    currentSession: enrichedCurrentSession,
    currentCallState,
    makeCall,
    hangup,
    acceptCall,
    rejectCall,
    sendDTMF,
    toggleMute,
    reinitialize,
  };
}